import React from "react";
import Icon from "components/icon";
import Typography from "components/typography";
import { TripCalculation, SEGMENT_COLORS } from "_interfaces/driverTripCalculator.interfaces";
import {
  formatCurrency,
  formatDistance,
  formatTime,
  formatSpeed,
} from "_services/modules/driverTripCalculator/calculateTrip";
import ConfirmationModal from "components/shared/confirmation-modal";
import { useAppTranslation } from "locale/useAppTranslation";
import PDFPreviewModal, { PDFPreviewModalHandle } from "./PDFPreviewModal";

interface DriverSlipViewProps {
  calculationResult: TripCalculation;
  onBack: () => void;
  onReset: () => void;
}

const DriverSlipView: React.FC<DriverSlipViewProps> = ({
  calculationResult,
  onBack,
  onReset,
}) => {
  const [showResetConfirmation, setShowResetConfirmation] = React.useState(false);
  const [showPDFModal, setShowPDFModal] = React.useState(false);
  const [isDirectDownloading, setIsDirectDownloading] = React.useState(false);
  const pdfModalRef = React.useRef<PDFPreviewModalHandle>(null);
  const { t } = useAppTranslation("driverTripCalculator");

  const handleResetConfirm = () => {
    setShowResetConfirmation(false);
    onReset();
  };

  const calculatedAt = new Date(calculationResult.created_at).toLocaleString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      <div className="space-y-5">
        {/* Trip Information Header */}
        <div>
          <div className="flex items-center justify-between">
            <Typography variant="heading3" className="text-sm font-bold text-primary-700 underline">
              {t("slip-trip-info-title")}
            </Typography>
            <span className="text-xs text-gray-500">{t("slip-calculated-at", { datetime: calculatedAt })}</span>
          </div>

          {/* Trip Info Summary */}
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-gray-500">{t("slip-field-customer")}</span>
              <p className="font-medium">{calculationResult.customer}</p>
            </div>
            <div>
              <span className="text-gray-500">{t("slip-field-temperature")}</span>
              <p className="font-medium">{calculationResult.temperature}°C</p>
            </div>
            <div>
              <span className="text-gray-500">{t("slip-field-plate-number")}</span>
              <p className="font-medium">{calculationResult.plate_number}</p>
            </div>
            <div>
              <span className="text-gray-500">{t("slip-field-vehicle-type")}</span>
              <p className="font-medium" data-testid="display-dtc-slip-vehicle-type">
                {calculationResult.vehicle_type.name}
              </p>
            </div>
            <div>
              <span className="text-gray-500">{t("slip-field-vehicle-model")}</span>
              <p className="font-medium" data-testid="display-dtc-slip-vehicle-model">
                {calculationResult.vehicle_model.name}
              </p>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Segments Breakdown */}
        {calculationResult.segments.map((segment, index) => {
          const segmentColor = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
          const expectedFuelLiters = segment.distance_km / segment.fuel_usage;

          return (
            <div key={segment.id} data-testid={`row-dtc-slip-segment-${index + 1}`}>
              {/* Segment Header */}
              <Typography
                variant="heading3"
                className="mb-3 text-sm font-bold underline"
                style={{ color: segmentColor }}
              >
                {t("slip-segment-title", { number: index + 1, tripType: segment.trip_type?.name || "Unknown" })}
              </Typography>

              {/* Route Locations */}
              <div className="mb-3 space-y-1" data-testid={`display-dtc-slip-locations-${index + 1}`}>
                {(() => {
                  const locationPoints = segment.locations.length > 0
                    ? [
                        { name: segment.locations[0].start_location_name, key: `start-${segment.locations[0].id || 0}`, hasToll: false },
                        ...segment.locations.map((loc, i) => ({
                          name: loc.end_location_name,
                          key: `end-${loc.id || i}`,
                          hasToll: loc.avoid_toll === false,
                        })),
                      ]
                    : [];

                  return locationPoints.map((point, ptIdx) => {
                    const isFirst = ptIdx === 0;
                    const isLast = ptIdx === locationPoints.length - 1;

                    return (
                      <div key={point.key} className="flex items-start gap-2 text-sm">
                        <div className="mt-1 flex flex-col items-center">
                          <div
                            className={`h-2.5 w-2.5 rounded-full ${
                              isFirst
                                ? "bg-primary-500"
                                : isLast
                                ? "bg-red-500"
                                : "border-2 border-gray-400"
                            }`}
                          />
                          {!isLast && <div className="mt-0.5 h-4 w-0.5 bg-gray-300" />}
                        </div>
                        <div className="flex-1">
                          <span className="text-gray-700">{point.name}</span>
                          {point.hasToll && !isFirst && (
                            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{t("slip-label-toll")}</span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Distance & Time */}
              <div
                className="mb-3 rounded-md bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700"
                data-testid={`display-dtc-slip-distance-${index + 1}`}
              >
                {/* {formatDistance(segment.distance_km)} · {formatSpeed(segment.distance_km, segment.estimated_time_minutes)} · {formatTime(segment.recommended_time_minutes)} ({t("slip-label-recommended")}) */}
                {formatDistance(segment.distance_km)} · {segment.avg_speed} {t("unit-km")}/{t("unit-hours")} · {formatTime(segment.recommended_time_minutes, t)} ({t("slip-label-recommended")})
                
              </div>

              <hr className="mb-3 border-gray-200" />

              {/* Cost Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t("slip-label-uang-jalan")}</span>
                  <span className="font-medium" data-testid={`display-dtc-slip-allowance-${index + 1}`}>
                    {formatCurrency(segment.driver_allowance)}
                  </span>
                </div>

                {/* Additional Costs */}
                {segment.additional_costs.length > 0 && (
                  <div data-testid={`list-dtc-slip-costs-${index + 1}`}>
                    <span className="text-xs text-gray-500">{t("slip-label-additional-cost")}</span>
                    {segment.additional_costs.map((cost) => (
                      <div key={cost.id} className="flex items-center justify-between pl-4">
                        <span className="text-gray-600">
                          {cost.cost_type === "toll" && t("slip-cost-toll")}
                          {cost.cost_type === "parking" && t("slip-cost-parking")}
                          {cost.cost_type === "loading" && t("slip-cost-loading")}
                          {cost.cost_type === "other" && t("slip-cost-other")}
                          {!["toll", "parking", "loading", "other"].includes(cost.cost_type) && cost.cost_type}
                        </span>
                        <span className="font-medium">{formatCurrency(cost.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Segment Total */}
                <div className="flex items-center justify-between border-t border-gray-200 pt-2 font-semibold">
                  <span>{t("slip-label-total")}</span>
                  <span className="text-primary-600">{formatCurrency(segment.segment_total)}</span>
                </div>

                {/* Expected Fuel Info */}
                <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                  <div className="flex items-center gap-1">
                    <Icon name="info" className="h-3.5 w-3.5" />
                    <span>
                      {t("slip-label-expected-fuel", { time: formatTime(segment.recommended_time_minutes, t) })} : {formatCurrency(segment.expected_fuel_cost)}
                    </span>
                  </div>
                  {(segment.rest_time > 0 || segment.idle_time > 0) && (
                    <div className="mt-1">
                      {t("slip-label-extra-fuel")} : {segment.rest_time + segment.idle_time} {t("unit-hours")}
                    </div>
                  )}
                </div>
              </div>

              {index < calculationResult.segments.length - 1 && (
                <hr className="mt-4 border-gray-200" />
              )}
            </div>
          );
        })}

        <hr className="border-gray-300" />

        {/* Grand Total */}
        <div className="flex items-center justify-between py-2">
          <Typography variant="heading2" className="text-lg font-bold text-text-primary">
            {t("slip-label-grand-total")}
          </Typography>
          <Typography
            variant="heading1"
            className="text-2xl font-bold text-text-primary"
            data-testid="display-dtc-slip-grand-total"
          >
            {formatCurrency(calculationResult.grand_total)}
          </Typography>
        </div>

        {/* Calculation Preview & Direct Download PDF Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowPDFModal(true)}
            className="flex-1 rounded-lg border border-primary-500 bg-white px-4 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50 transition-colors"
            data-testid="btn-dtc-slip-preview"
          >
            {t("btn-calculation-preview")}
          </button>
          <button
            type="button"
            onClick={async () => {
              setIsDirectDownloading(true);
              try {
                await pdfModalRef.current?.download();
              } finally {
                setIsDirectDownloading(false);
              }
            }}
            disabled={isDirectDownloading}
            className="flex-1 rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-600 transition-colors disabled:opacity-60"
            data-testid="btn-dtc-slip-download"
          >
            {isDirectDownloading ? t("modal-pdf-downloading") : t("btn-download-pdf")}
          </button>
        </div>

        {/* Back and Reset Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="btn-dtc-slip-back"
          >
            {t("btn-back")}
          </button>
          <button
            type="button"
            onClick={() => setShowResetConfirmation(true)}
            className="flex-1 rounded-lg bg-red-500 px-4 py-3 text-sm font-medium text-white hover:bg-red-600 transition-colors"
            data-testid="btn-dtc-slip-reset"
          >
            {t("btn-reset")}
          </button>
        </div>
      </div>

      <ConfirmationModal
        open={showResetConfirmation}
        onCancel={() => setShowResetConfirmation(false)}
        onConfirm={handleResetConfirm}
        title={t("modal-reset-title")}
        message={t("modal-reset-message")}
        confirmLabel={t("modal-reset-confirm")}
        cancelLabel={t("btn-cancel")}
        testId="reset-confirm-modal-dtc-view"
      />

      <PDFPreviewModal
        ref={pdfModalRef}
        open={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        calculationResult={calculationResult}
      />
    </>
  );
};

export default DriverSlipView;
